import { useState, useEffect, useContext } from "react";
import { StepDates } from "./steps/stepDates";
import { Box, Container, Typography, Grid, Paper, Stack } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useFormik } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { StepAmenities } from "./steps/stepAmenities";
import { BookingOverview } from "./bookingOverview";
import { checkAmenityAvailable } from "Apis/amenity";
import { bookAmenity } from "Apis/amenity";
import { addOnsList } from "Apis/addOns";
import { LoginFormContext } from "context/LoginFormContext";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthRouteJson } from "helpers/constant";

const STEPS = ["Dates & Slot", "Select Amenities"];

const groupAmenitiesByDate = (rows = []) => {
  const map = {};
  rows?.forEach(({ date, amenities = [] }) => {
    if (!map[date]) map[date] = [];
    amenities?.forEach((a) => {
      if (!map[date]?.some((x) => x?.id === a?.id)) map[date]?.push(a);
    });
  });
  return Object.keys(map)
    .sort((a, b) => dayjs(a).diff(dayjs(b)))
    .map((date) => ({ date, amenities: map[date] }));
};

const validationSchema = Yup.object({
  start_date: Yup.mixed().required("Start date is required"),
  end_date: Yup.mixed().required("End date is required"),
  slot: Yup.string().required("Please select a slot"),
  occasion: Yup.string().required("Occasion is required"),
  memberType: Yup.string().required("Required"),
  name: Yup.string().when("memberType", {
    is: "nonMember",
    then: (s) => s.required("Name is required"),
    otherwise: (s) => s.notRequired(),
  }),
  mobile: Yup.string().when("memberType", {
    is: "nonMember",
    then: (s) =>
      s
        .matches(/^[6-9]\d{9}$/, "Enter valid 10-digit number")
        .required("Mobile number is required"),
    otherwise: (s) => s.notRequired(),
  }),
  memberNumber: Yup.string().when("memberType", {
    is: "member",
    then: (s) => s.required("Head member is required"),
    otherwise: (s) => s.notRequired(),
  }),
  selectedAmenities: Yup.object().test(
    "at-least-one-amenity",
    "Select at least one amenity for any date",
    (val) => Object.values(val || {}).some((arr) => arr?.length > 0)
  ),
  selectedAddOns: Yup.object().optional(),
});

export default function Booking() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setNotification } = useContext(LoginFormContext);
  const [activeStep, setActiveStep] = useState(0);
  const [availableAmenities, setAvailableAmenities] = useState([]);
  const [loadingAmenities, setLoadingAmenities] = useState(false);
  const [loadingAddOns, setLoadingAddOns] = useState(false);
  const [availableAddons, setAvailableAddons] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [appliedDiscount, setAppliedDiscount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [subTotal, setSubTotal] = useState(0);

  const handleSubmit = async (data) => {
    setLoading(true);
    const allDates = Array.from(
      new Set([
        ...Object.keys(data?.selectedAmenities || {}),
        ...Object.keys(data?.selectedAddOns || {}),
      ])
    );

    const bookings = allDates
      .map((date) => ({
        date,
        amenity_ids: data?.selectedAmenities?.[date] || [],
        amenity_addons_ids: data?.selectedAddOns?.[date] || [],
      }))
      .filter((b) => b?.amenity_ids?.length > 0 || b?.amenity_addons_ids?.length > 0);

    const payload = {
      occasion_id: data?.occasion_id,
      member_id: data?.memberType === "member" ? data?.member_id : null,
      start_date: dayjs(data?.start_date).format("YYYY-MM-DD"),
      end_date: dayjs(data?.end_date).format("YYYY-MM-DD"),
      slot: data?.slot,
      is_guest_customer: data?.memberType === "nonMember" ? 1 : 0,
      total_amount: grandTotal,
      discount_amount: appliedDiscount,
      member_name: data?.name,
      member_mobile_number: data?.mobile,
      subtotal_amount: subTotal,
      bookings,
    };

    const response = await bookAmenity(payload);
    if (response?.status === 200) {
      setLoading(false);
      navigate(AuthRouteJson?.booking);
      setNotification({
        color: "success",
        icon: "check",
        title: "Success",
        content: response?.data?.message,
        open: true,
      });
    } else {
      setLoading(false);
      setNotification({
        color: "error",
        icon: "check",
        title: "error",
        content: response?.data?.message,
        open: true,
      });
    }
  };

  const initialBookingDate = location?.state?.bookingDate
    ? dayjs(location?.state?.bookingDate)
    : null;
  const initialSlot = location?.state?.slotNum || "";

  const formik = useFormik({
    initialValues: {
      start_date: initialBookingDate,
      end_date: initialBookingDate,
      slot: initialSlot,
      occasion: "",
      memberType: "member",
      name: "",
      mobile: "",
      memberNumber: "",
      selectedAmenities: {},
      selectedAddOns: {},
    },
    validationSchema,
    validateOnChange: true,
    onSubmit: handleSubmit,
  });
  const { values, setFieldValue, errors, touched, handleChange, handleBlur } = formik;
  const isMember = values?.memberType === "member";
  const priceKey = isMember ? "member" : "nonMember";

  const canStep0 = Boolean(
    values?.start_date &&
      values?.end_date &&
      values?.slot &&
      values?.occasion &&
      values?.memberType &&
      (values?.memberType === "member"
        ? values?.member_id
        : values?.name && values?.mobile && values?.mobile?.length === 10)
  );

  // True if any date has at least one amenity selected.
  const canStep1 = Object.values(values?.selectedAmenities || {}).some((arr) => arr?.length > 0);

  const handelCheckAmenityAvailable = async () => {
    setLoadingAmenities(true);
    const data = {
      occasion_id: values?.occasion_id,
      start_date: dayjs(values?.start_date).format("YYYY-MM-DD"),
      end_date: dayjs(values?.end_date).format("YYYY-MM-DD"),
      slot: values?.slot,
    };

    // ── Real API call (kept for when the backend is ready) ──
    const response = await checkAmenityAvailable(data);
    if (response?.status === 200) {
      setAvailableAmenities(groupAmenitiesByDate(response?.data?.data?.rows));
      setLoadingAmenities(false);
      setActiveStep(1);
    }
  };

  useEffect(() => {
    if (values?.start_date && values?.end_date && values?.slot) {
      setFieldValue("selectedAmenities", {});
      setFieldValue("selectedAddOns", {});
    }
  }, [values?.start_date, values?.end_date, values?.slot]);

  const getAddOnsList = async () => {
    setLoadingAddOns(true);
    const response = await addOnsList({});
    if (response?.status === 200) {
      setAvailableAddons(response?.data?.data?.rows);
      setLoadingAddOns(false);
    }
  };

  useEffect(() => {
    getAddOnsList();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box sx={{ minHeight: "100vh", backgroundColor: "#f0f2f5" }}>
          <Container maxWidth="lg" sx={{ mt: 4, pb: 6 }}>
            <form onSubmit={formik?.handleSubmit}>
              <Grid container spacing={3} alignItems="flex-start">
                {/* ── LEFT: Step Content ── */}
                <Grid item xs={12} lg={8}>
                  {/* ── Stepper ── */}
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      p: { xs: 2, md: 3 },
                      mb: 3,
                      border: `1px solid #dee2e6`,
                      backgroundColor: "#ffffff",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                    >
                      {STEPS?.map((label, i) => {
                        const isActive = activeStep === i;
                        return (
                          <Box
                            key={label}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              flex: 1,
                              position: "relative",
                            }}
                          >
                            <Box
                              sx={{
                                width: 32,
                                height: 32,
                                borderRadius: "50%",
                                backgroundColor: isActive ? "#A68181" : "#e0e0e0",
                                color: isActive ? "#fff" : "#837e7e",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontWeight: 600,
                              }}
                            >
                              {i + 1}
                            </Box>
                            <Typography
                              variant="caption"
                              sx={{
                                mt: 1,
                                fontWeight: isActive ? 700 : 500,
                                color: "#A68181",
                                textAlign: "center",
                              }}
                            >
                              {label}
                            </Typography>
                            {i !== STEPS?.length - 1 && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  top: "16px",
                                  left: "50%",
                                  right: "-50%",
                                  height: "2px",
                                  backgroundColor: "#e0e0e0",
                                  zIndex: -1,
                                }}
                              />
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  </Paper>

                  {/* STEP 0 – Dates & Slot */}
                  {activeStep === 0 && (
                    <StepDates
                      values={values}
                      handleChange={handleChange}
                      isMember={isMember}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      errors={errors}
                      handleBlur={handleBlur}
                      canStep0={canStep0}
                      setActiveStep={setActiveStep}
                      handelCheckAmenityAvailable={handelCheckAmenityAvailable}
                    />
                  )}

                  {/* STEP 1 – Amenities (now date-wise accordion) */}
                  {activeStep === 1 && (
                    <StepAmenities
                      values={values}
                      touched={touched}
                      errors={errors}
                      priceKey={priceKey}
                      setFieldValue={setFieldValue}
                      loadingAmenities={loadingAmenities}
                      availableAmenities={availableAmenities}
                      canStep1={canStep1}
                      setActiveStep={setActiveStep}
                      loadingAddOns={loadingAddOns}
                      availableAddons={availableAddons}
                    />
                  )}
                </Grid>

                {/* ── RIGHT: Summary ── */}
                <BookingOverview
                  values={values}
                  isMember={isMember}
                  priceKey={priceKey}
                  availableAmenities={availableAmenities}
                  availableAddons={availableAddons}
                  setGrandTotal={setGrandTotal}
                  grandTotal={grandTotal}
                  activeStep={activeStep}
                  canStep1={canStep1}
                  setAppliedDiscount={setAppliedDiscount}
                  appliedDiscount={appliedDiscount}
                  loading={loading}
                  setSubTotal={setSubTotal}
                />
              </Grid>
            </form>
          </Container>
        </Box>
      </LocalizationProvider>
    </DashboardLayout>
  );
}
